import { useState } from "react";
import { createPortal } from "react-dom";
import { useOverlay } from "./Overlay";
import { thumbUrl, type ThumbTier } from "../../../../src/image-thumb.ts";

/**
 * 可点击放大的缩略图。**所有展示图片的地方都用它**（leo 2026-08-27：
 * 「我们所有图片都能点击放大，使用一个组件」）。
 *
 * 之前 8 个文件里各写各的 `<img>`：卡面、封面、建筑素材、拼图、idol 头像、
 * 玩家头像……全是死图，运营想看清楚只能把浏览器缩放拉大，或者去 CDN 开链接。
 * 一处一处加放大必然漏，也必然长出好几种交互，所以收进一个组件。
 *
 * 交互刻意做薄：点开一层浮层看原图，点任意处或 Esc 关掉。
 *
 * **缩略图默认显示 CDN 缩过的小图，放大层才用原图**（leo 2026-10-23）：原图常是几百 KB 到几 MB，
 * 一页几十张原图塞进 36px 的格子，页面先空白再一张张冒出来。小图怎么拼见 src/image-thumb.ts，
 * 不认识的域名原样给原图；小图万一加载失败，退回原图再试一次。
 * 不做缩放/翻页/下载——那些都还没有人要，加了就是猜需求。
 */
export function Thumb({
  src, alt = "", className = "", title, onError, tier = "icon",
}: {
  /** 图片地址；空/null 时不渲染任何东西，由调用方决定占位样式 */
  src?: string | null;
  alt?: string;
  /** 缩略图尺寸与圆角由调用方给，组件不替它定 */
  className?: string;
  /** 放大层上的标题，通常是卡名/素材名 */
  title?: string;
  /** 图挂了的回调。头像那类有占位兜底的调用方要用它切回占位，不能直接留个碎图 */
  onError?: () => void;
  /** 缩略图用哪一档小图。默认 icon（320 宽）：管端缩略图最大约 150px，2 倍屏也够清楚，且与游戏头像 / 图标同档，CDN 不多算一次缩图 */
  tier?: ThumbTier;
}) {
  const [open, setOpen] = useState(false);
  // 小图挂了（域名没开缩图之类）退回原图；原图也挂了才交给调用方的 onError
  const [thumbFailed, setThumbFailed] = useState<string | null>(null);
  // Image previews participate in the same stack as their owning editor.
  // A separate Escape listener closed both layers and left body scroll locked.
  useOverlay(() => setOpen(false), open);

  if (!src) return null;
  const small = thumbUrl(src, tier);
  const shown = thumbFailed === src ? src : small;
  return (
    <>
      <img
        src={shown}
        alt={alt}
        title={title}
        onError={() => { if (shown !== src) setThumbFailed(src); else onError?.(); }}
        // inline-block 不能省：Tailwind preflight 把 img 设成 display:block，
        // 于是它无视单元格的 text-center 贴着左边——表头居中、图靠左，看起来就是错位。
        // align-middle 顺手掐掉 inline 元素的基线留白。
        className={`inline-block align-middle ${className} cursor-zoom-in`}
        // 缩略图常常躺在可点的行/单元格里，放大不该顺带触发那一层的点击
        // mousedown 也挡：有些祖先（拖拽、选中态）听的是 mousedown 而不是 click
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      />
      {open && createPortal(
        /*
         * 关大图不能连累底下的祖先。Thumb 常躺在可点的祖先里（表格行、上传按钮），
         * 素材抽屉里就出过「关掉大图顺手弹出文件选择器」（codex 复审 2026-08-27）：
         * 原本只在 mousedown 上 stopPropagation，浮层随即卸载，同一次按下的 click
         * 就落到底下的按钮上了。
         *
         * 两件事**缺一不可**，别只留一个：
         * 1. 关闭挂在 click 上（不是 mousedown），并在 click 上 stopPropagation——
         *    ⚠️ **portal 挡不住这一层**：React 合成事件按 **React 树**冒泡而不是 DOM 树，
         *    portal 出去的浮层，事件照样冒到 React 里的祖先。拦住它的是 stopPropagation。
         * 2. portal 到 body——挡的是**原生** DOM 冒泡，以及祖先 overflow/z-index
         *    对浮层的裁剪（抽屉里的卡面否则会被裁掉一半）。
         */
        <div
          // z 比 Modal(60)/Drawer 高：抽屉里的卡面也要能放大，压在抽屉底下就白做了
          className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-3 bg-black/70 p-6"
          // 关闭放在 click 而不是 mousedown：mousedown 就卸载的话，
          // 同一次按下的 click 会落到浮层下面那一层去
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {title && <div className="max-w-full truncate text-sm text-white/90">{title}</div>}
          <img
            src={src}
            alt={alt}
            className="max-h-[82vh] max-w-[92vw] cursor-zoom-out rounded-base object-contain shadow-light"
          />
        </div>,
        document.body,
      )}
    </>
  );
}
